import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@bikdotai/bik-component-library";
import { ServiceScreenFeaturesService } from "../services/ServiceScreenFeaturesService";
import {
  ServiceScreenFeature,
  FeatureFilters,
} from "../models/serviceFeatures/ServiceScreenFeatureModel";
import FeaturesList from "./components/FeaturesList";
import FeatureForm from "./components/FeatureForm";
import BulkUploadModal from "./components/BulkUploadModal";
import { saveAs } from "file-saver";

const ServiceFeaturesDashboard: React.FC = () => {
  const [allFeatures, setAllFeatures] = useState<ServiceScreenFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FeatureFilters>({});

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<ServiceScreenFeature | undefined>();

  const service = new ServiceScreenFeaturesService();

  const fetchFeatures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await service.getFeaturesList();
      setAllFeatures(response.response.features);
    } catch (err: any) {
      setError(err.message || "Failed to fetch features");
      console.error("Error fetching features:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const handleCreateFeature = async (featureData: Partial<ServiceScreenFeature>) => {
    try {
      await service.createFeature(featureData as any);
      setShowCreateModal(false);
      fetchFeatures();
    } catch (err: any) {
      throw err;
    }
  };

  const handleUpdateFeature = async (featureData: Partial<ServiceScreenFeature>) => {
    if (!editingFeature) return;
    try {
      await service.updateFeature(editingFeature.feature_id, featureData);
      setShowEditModal(false);
      setEditingFeature(undefined);
      fetchFeatures();
    } catch (err: any) {
      throw err;
    }
  };

  const handleDeleteFeature = async (featureId: string) => {
    if (!window.confirm("Are you sure you want to delete this feature?")) {
      return;
    }
    try {
      await service.deleteFeature(featureId);
      fetchFeatures();
    } catch (err: any) {
      alert(`Error deleting feature: ${err.message || "Please try again"}`);
    }
  };

  const handleEdit = (feature: ServiceScreenFeature) => {
    setEditingFeature(feature);
    setShowEditModal(true);
  };

  const handleBulkUploadSuccess = () => {
    fetchFeatures();
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await service.downloadFeaturesList();
      const downloadUrl = response.response.downloadUrl;

      // If it's a GCS URL (gs://), we need to handle it differently
      // For now, try to fetch it if it's an HTTP URL
      if (downloadUrl.startsWith("http://") || downloadUrl.startsWith("https://")) {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        saveAs(blob, `features-export-${new Date().getTime()}.xlsx`);
      } else if (downloadUrl.startsWith("gs://")) {
        // For GCS URLs, we might need to use a proxy or convert to signed URL
        // For now, show the URL to the user
        alert(
          `Download URL: ${downloadUrl}\n\nPlease contact support to download the file, or use the GCS console.`
        );
      } else {
        alert(`Download URL: ${downloadUrl}`);
      }
    } catch (err: any) {
      alert(`Error downloading features: ${err.message || "Please try again"}`);
    }
  };

  const handleFiltersChange = (newFilters: FeatureFilters) => {
    setFilters(newFilters);
  };

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 style={{ fontSize: "24px", fontWeight: "600", margin: 0 }}>
          Service Features Management
        </h1>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            buttonText="Download Excel"
            onClick={handleDownloadExcel}
            size="medium"
            style={{
              backgroundColor: "#059669",
              color: "white",
            }}
          />
          <Button
            buttonText="Bulk Upload"
            onClick={() => setShowBulkUploadModal(true)}
            size="medium"
            style={{
              backgroundColor: "#731DCF",
              color: "white",
            }}
          />
          <Button
            buttonText="Create Feature"
            onClick={() => setShowCreateModal(true)}
            size="medium"
            style={{
              backgroundColor: "#731DCF",
              color: "white",
            }}
          />
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "12px",
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          Error: {error}
        </div>
      )}

      <FeaturesList
        allFeatures={allFeatures}
        loading={loading}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onEdit={handleEdit}
        onDelete={handleDeleteFeature}
      />

      {/* Create Feature Modal */}
      <FeatureForm
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateFeature}
        mode="create"
      />

      {/* Edit Feature Modal */}
      {editingFeature && (
        <FeatureForm
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingFeature(undefined);
          }}
          onSubmit={handleUpdateFeature}
          initialData={editingFeature}
          mode="edit"
        />
      )}

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={handleBulkUploadSuccess}
      />
    </div>
  );
};

export default ServiceFeaturesDashboard;


